/**
 * Sammelüberweisung: aus mehreren markierten Rechnungen die drei Werte bauen,
 * die im Online-Banking gebraucht werden — Verwendungszweck, Betrag, IBAN.
 *
 * Bewusst als reine Funktion ohne React: die Fallen dieses Features sind
 * fachlicher Natur (Skonto, Teilzahlungen, Gutschriften, fremde Empfänger),
 * und die sollen an EINER Stelle stehen statt verteilt im JSX.
 */
import { getPaymentDeadline, openAmount } from './invoiceUtils'

/** SEPA-Verwendungszweck: 140 Zeichen, danach schneidet die Bank ab. */
export const SEPA_REFERENCE_MAX = 140

export interface BatchInvoice {
  id: number
  type?: string | null
  status?: string | null
  invoice_number?: string | null
  supplier_name?: string | null
  supplier_iban?: string | null
  gross_amount?: number | null
  paid_amount?: number | null
  total_credits_applied?: number | null
  skonto_percent?: number | null
  skonto_amount?: number | null
  skonto_date?: string | null
  due_date?: string | null
  invoice_date?: string | null
}

export interface PaymentBatch {
  count: number
  /** Verwendungszweck, wie er kopiert wird — ggf. schon auf 140 Zeichen gekürzt. */
  reference: string
  /** Ungekürzte Verkettung, nur zur Anzeige der echten Länge. */
  referenceFull: string
  /** Rechnungsnummern, die wegen des 140-Zeichen-Limits nicht mehr hineinpassen. */
  droppedNumbers: string[]
  /** Rechnungen ohne Nummer — die tauchen im Verwendungszweck gar nicht auf. */
  missingNumberCount: number
  /** Offene Summe vor Skontoabzug. */
  openTotal: number
  /** Summe der ziehbaren Skontobeträge (nur laufende Fristen, nur gedruckte Werte). */
  skontoTotal: number
  /** Anzahl Rechnungen mit laufender Skontofrist. */
  skontoCount: number
  /** Der Betrag, der überwiesen wird — openTotal, abzüglich Skonto wenn gewünscht. */
  total: number
  iban: string | null
  recipient: string | null
  /** Unterschiedliche Empfänger/IBANs in der Auswahl → keine Sammelüberweisung. */
  recipientConflict: boolean
  /** Verschiedene Empfängernamen, alphabetisch — für die Warnmeldung. */
  distinctRecipients: string[]
  /** Rechnungen ohne hinterlegte IBAN. */
  missingIbanCount: number
}

/**
 * Darf diese Zeile in eine Sammelüberweisung?
 *
 * Nur was wir selbst überweisen: Eingangsrechnungen und Gutschriften (die
 * mindern die Summe), und nur solange offen. Mahnungen sind bewusst draußen —
 * bezahlt wird die Rechnung dahinter, nicht die Mahnung (gilt für die
 * Kindzeilen unter einer Rechnung wie für den Mahnungen-Tab).
 */
const PAYABLE_TYPES = ['incoming', 'credit_note']
const CLOSED_STATUSES = ['paid', 'resolved', 'rejected']

export function isPayableRow(row: { kind?: string; type?: string | null; status?: string | null } | null | undefined): boolean {
  if (!row || row.kind === 'reminder') return false
  if (!PAYABLE_TYPES.includes(row.type ?? '')) return false
  return !CLOSED_STATUSES.includes(row.status ?? '')
}

/** Vergleichsform einer IBAN: ohne Leerzeichen, Großbuchstaben. */
export const normalizeIban = (iban?: string | null): string =>
  (iban || '').replace(/\s+/g, '').toUpperCase()

/** IBAN in Vierergruppen — nur zur Anzeige, kopiert wird die kompakte Form. */
export const formatIban = (iban?: string | null): string =>
  normalizeIban(iban).replace(/(.{4})/g, '$1 ').trim()

/**
 * Grobe Vergleichsform eines Lieferantennamens. Die echte Alias-Auflösung
 * (`services/supplierAliases.js`) sitzt im Backend; hier reicht es, Rechtsform
 * und Zeichensetzung wegzuwerfen, damit "Obeta GmbH" und "Obeta" nicht als
 * zwei Empfänger gelten und eine Sammelüberweisung grundlos blockieren.
 */
function normalizeSupplier(name?: string | null): string {
  return (name || '')
    .toLowerCase()
    .replace(/\b(gmbh|mbh|ag|kg|ohg|gbr|se|co|kgaa|e\.?k\.?|ug|haftungsbeschränkt)\b/g, '')
    .replace(/[^a-zäöüß0-9]+/g, '')
    .trim()
}

const round2 = (n: number) => Math.round(n * 100) / 100

/**
 * Skontoabzug einer einzelnen Rechnung — oder 0.
 *
 * NIE aus Brutto × Satz gerechnet: Fracht, Entsorgung und Dienstleistung sind
 * nicht skontierfähig, deshalb zählt ausschließlich der im Beleg gedruckte
 * Betrag (#125). Ohne gedruckten Wert wird nichts gezogen — lieber den vollen
 * Betrag überweisen als zu wenig und eine Mahnung kassieren.
 */
export function skontoDeduction(inv: BatchInvoice): number {
  const deadline = getPaymentDeadline(inv)
  if (!deadline.skontoActive) return 0
  return deadline.skontoAmount ?? 0
}

/**
 * Offener Betrag einer Rechnung: Brutto − verrechnete Gutschriften − Teilzahlungen.
 * Gutschriften selbst stehen negativ in den Büchern (`enforceCreditNoteSigns`)
 * und mindern die Summe dadurch von allein.
 */
export function payableAmount(inv: BatchInvoice): number {
  return openAmount(inv as any, [{ applied_amount: inv.total_credits_applied ?? 0 }])
}

/**
 * Baut die Sammelüberweisung. `invoices` kommt in der Reihenfolge der Tabelle —
 * der Verwendungszweck liest sich dann so, wie die Liste aussieht.
 */
export function buildPaymentBatch(
  invoices: BatchInvoice[],
  options: { applySkonto?: boolean } = {},
): PaymentBatch {
  const applySkonto = options.applySkonto !== false

  // Doppelte Nummern (dieselbe Rechnung zweimal im Beleg, Sammelmahnungen)
  // würden den Verwendungszweck unnötig aufblähen.
  const numbers: string[] = []
  let missingNumberCount = 0
  for (const inv of invoices) {
    const nr = (inv.invoice_number || '').trim()
    if (!nr) { missingNumberCount++; continue }
    if (!numbers.includes(nr)) numbers.push(nr)
  }

  const referenceFull = numbers.join(', ')

  // Kürzen an der Nummerngrenze: eine halbe Rechnungsnummer im Verwendungszweck
  // hilft der Buchhaltung des Lieferanten nicht weiter.
  let reference = referenceFull
  const droppedNumbers: string[] = []
  if (referenceFull.length > SEPA_REFERENCE_MAX) {
    const kept: string[] = []
    for (const nr of numbers) {
      const candidate = [...kept, nr].join(', ')
      if (candidate.length <= SEPA_REFERENCE_MAX) kept.push(nr)
      else droppedNumbers.push(nr)
    }
    reference = kept.join(', ')
  }

  const openTotal = round2(invoices.reduce((s, inv) => s + payableAmount(inv), 0))
  const skontoRelevant = invoices.filter((inv) => skontoDeduction(inv) > 0)
  const skontoTotal = round2(skontoRelevant.reduce((s, inv) => s + skontoDeduction(inv), 0))
  const total = round2(applySkonto ? openTotal - skontoTotal : openTotal)

  // Empfänger bestimmen. Die IBAN entscheidet, wohin das Geld geht — steht sie
  // überall und ist überall gleich, ist der Fall klar, egal wie der Name im OCR
  // geschrieben wurde. Fehlt sie irgendwo, muss der NAME die Auswahl decken.
  const ibans = invoices.map((inv) => normalizeIban(inv.supplier_iban))
  const distinctIbans = [...new Set(ibans.filter(Boolean))]
  const missingIbanCount = ibans.filter((i) => !i).length

  const distinctSupplierKeys = [...new Set(invoices.map((inv) => normalizeSupplier(inv.supplier_name)))]
  const distinctRecipients = [...new Set(
    invoices.map((inv) => (inv.supplier_name || '').trim()).filter(Boolean),
  )].sort((a, b) => a.localeCompare(b, 'de'))

  const allSameIban = distinctIbans.length === 1 && missingIbanCount === 0
  const sameSupplier = distinctSupplierKeys.length === 1 && distinctIbans.length <= 1
  const recipientConflict = invoices.length > 0 && !allSameIban && !sameSupplier

  return {
    count: invoices.length,
    reference,
    referenceFull,
    droppedNumbers,
    missingNumberCount,
    openTotal,
    skontoTotal,
    skontoCount: skontoRelevant.length,
    total,
    iban: recipientConflict ? null : (distinctIbans[0] ?? null),
    recipient: recipientConflict ? null : (distinctRecipients[0] ?? null),
    recipientConflict,
    distinctRecipients,
    missingIbanCount,
  }
}

/**
 * Betrag fürs Bankformular: deutsches Komma, KEIN Tausendertrennzeichen —
 * "1.234,56" wird von manchen Eingabefeldern als 1,23 gelesen.
 */
export const formatAmountForClipboard = (amount: number): string =>
  amount.toFixed(2).replace('.', ',')
